<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col>
        <loading-function-placeholder />
      </v-col>
    </v-row>

    <v-row v-else-if="_.isEmpty(hmdFunction)">
      <new-jumbotron
        :my-jumbotron="{
          type: 'accessFunction',
          heading: 'You don\'t have access to this information',
          subheading: 'Ask the owner to give you the permissions to access this dataset.',
          elementList: [],
          dividerHeading: 'Contact your superior for further explanations...',
        }"
      />
    </v-row>
    <v-row v-else>
      <v-col
        cols="12"
        class="pb-0 pt-0"
      >
        <!-- Toolbar -->
        <v-toolbar
          flat
          text
          height="88px"
        >
          <v-icon left>
            {{ $appConfig.function.icon }}
          </v-icon>
          <v-toolbar-title>
            Function : "{{ hmdFunction.name }}"
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-select
              v-model="selectedEnv"
              clearable
              outlined
              class="mt-2"
              :prepend-inner-icon="(selectedEnv!=='' && typeof selectedEnv !='undefined')?'mdi-eye':'mdi-settings'"
              :items="hmdFunction.environments"
              item-text="name"
              item-value="id"
              placeholder="Environment"
              required
              @change="saveFunctionEnvironmentPreference"
              @click:prepend-inner="handlePrependEnvIconClick"
            />
            <span
              class="font-weight-bold ml-3 mt-5"
            >Status : {{ (hmdFunction.enabled ? "enabled" : "disabled") }}
            </span>
            <v-switch
              v-model="hmdFunction.enabled"
              :disabled="!hasOwnerPermissions"
              color="accent"
              class="mt-5"
              hide-details
              @change="changeStatus()"
            />
            <v-dialog
              v-model="manageEnvsDialog"
              width="50%"
            >
              <v-card>
                <function-environment-create-update @updateFunctionEnvs="(envs)=>{hmdFunction.environments = envs}" />
              </v-card>
            </v-dialog>
            <v-dialog
              v-if="selectedEnv"
              v-model="showEnvDialog"
              width="50%"
            >
              <v-card>
                <function-environment-view
                  :dialog-visibility="showEnvDialog"
                  :environment="selectedEnv"
                  @manageEnvironments="()=>{showEnvDialog=false;manageEnvsDialog=true}"
                />
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>
        <!-- Toolbar end -->
      </v-col>

      <v-col
        cols="12"
        class="pt-0"
      >
        <v-tabs
          v-model="activeTab"
          fixed-tabs
          background-color="grey lighten-3"
        >
          <v-tab
            v-for="(item, index) in menuItems"
            :key="index"
            class="ml-0"
            ripple
          >
            {{ item.value }}
          </v-tab>
          <v-tab-item
            v-for="(item, index) in menuItems"
            :key="index"
            :transition="false"
            :reverse-transition="false"
          >
            <template v-if="item.value === 'code'">
              <function-code
                :hmd-function="hmdFunction"
                :code="code"
                :save-loading-button="saveLoadingButton"
                :templates-prop="templates"
                @save="saveCode"
                @change="changeCode"
                @loadTemplates="loadTemplates"
                @updateCloseConstraint="e=>$emit('updateCloseConstraint',e)"
              />
            </template>
            <template v-else-if="item.value === 'integration'">
              <function-integration
                :hmd-function="hmdFunction"
                :tabs="tabs"
                :code-to-copy-prop="codeToCopy"
                :current-language-prop="currentLanguage"
                :current-action-prop="currentAction"
                @changeLanguage="changeLanguage"
                @changeAction="changeAction"
              />
            </template>
            <template v-else-if="item.value === 'trigger'">
              <function-trigger
                :hmd-function="hmdFunction"
                :search="search"
                :selected-items="selectedItems"
                @searchHandler="searchHandler"
                @deleteTrigger="deleteTrigger"
                @selectedItemsHandler="selectedItemsHandler"
              />
            </template>
            <template v-else-if="item.value === 'monitoring'">
              <function-monitoring
                :hmd-function="hmdFunction"
                :metrics="metrics"
                @refreshMonitoringData="refreshMonitoringData"
              />
            </template>
            <template v-else-if="item.value === 'permission'">
              <function-permissions
                :hmd-function="hmdFunction"
                @modifyPermissions="modifyPermissions"
              />
            </template>
            <template v-else-if="item.value === 'settings'">
              <function-settings
                :hmd-function="hmdFunction"
                :is-new="false"
                :labels-prop="labels"
                @delete="$emit('delete')"
              />
            </template>
          </v-tab-item>
        </v-tabs>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
  import LoadingFunctionPlaceholder from './../../../UIComponents/Placeholders/LoadingFunctionPlaceholder'
  import FunctionService from './services/FunctionService'
  import FunctionCode from './FunctionCode.vue'
  import FunctionTrigger from './FunctionTrigger.vue'
  import FunctionSettings from './FunctionCreateUpdate.vue'
  // import FunctionMonitoring from './FunctionMonitoring.vue'
  import FunctionIntegration from './FunctionIntegration.vue'
  import FunctionPermissions from './FunctionPermissions.vue'
  import HmdService from '../../../UIComponents/services/HmdService'

  import functionEnvView from './../Functions/FunctionEnvironmentView'
  import functionEnvCreateUpdate from './../Functions/FunctionEnvironmentCreateUpdate'

  import NewJumbotron from '../../../UIComponents/Jumbotron.vue'

  export default {
    components: {
      LoadingFunctionPlaceholder,
      'new-jumbotron': NewJumbotron,
      'function-environment-view': functionEnvView,
      'function-environment-create-update': functionEnvCreateUpdate,
      FunctionCode,
      FunctionTrigger,
      FunctionIntegration,
      FunctionSettings,
      // FunctionMonitoring,
      FunctionPermissions,
    },
    props: {
      identifier: {
        type: String,
        default: null,
      },
    },
    data: function () {
      return {
        isLoading: true,
        hmdFunction: {},
        activeTab: 0,
        code: '',
        menuItems: [],
        search: '',
        selectedItems: 'All',
        saveLoadingButton: false,
        metrics: {},
        labels: [],
        tabs: {},
        codeToCopy: '',
        currentLanguage: '',
        currentAction: '',
        templates: [{ content: '' }],
        lastSavedCode: '',
        selectedEnv: '',
        envItems: [{ name: 'Environnement 1', id:'1' }, { name: 'env 2', id:'2'}, { name: 'Environnement 3', id:'3'}],
        manageEnvsDialog: false,
        showEnvDialog: false,
      }
    },
    computed: {
      hasOwnerPermissions () {
        return this.hmdFunction.user_permissions.includes('owner_function') ||
        this.hmdFunction.user_permissions.includes('owner_project')
      },
      hasWritePermissions () {
        return this.hasOwnerPermissions ||
        this.hmdFunction.user_permissions.includes('read_write_function') ||
        this.hmdFunction.user_permissions.includes('read_write_project')
      },
      hasReadPermissions () {
        return this.hasWritePermissions ||
        this.hmdFunction.user_permissions.includes('read_function') ||
        this.hmdFunction.user_permissions.includes('read_project')
      },
    },
    watch: {
      activeTab: function () {
        this.loadTab(this.activeTab)
      },
    },
    mounted () {
      this.isLoading = true
      FunctionService.get(this.identifier || this.$route.params.identifier, parent)
        .then(result => {
          this.hmdFunction = result.data
          if (this.hmdFunction.content === null) this.hmdFunction.content = ''
          // this.code = atob(this.hmdFunction.content)
          this.lastSavedCode = this.hmdFunction.content
          this.code = this.hmdFunction.content
          if (typeof (this.hmdFunction.user_permissions) === 'undefined') {
            this.hmdFunction.user_permissions = null
          }
          // this.hmdFunction.user_permissions = 'owner_function'
          if (!this.hasOwnerPermissions) {
            this.menuItems = [
              { value: 'code', loaded: false },
              { value: 'integration', loaded: false },
              { value: 'permission', loaded: false },
            ]
          } else {
            this.menuItems = [
              { value: 'code', loaded: false },
              { value: 'integration', loaded: false },
              { value: 'trigger', loaded: false },
              { value: 'permission', loaded: false },
              // {value: 'monitoring', loaded: false},
              { value: 'settings', loaded: false },
            ]
          }
          this.loadTab(this.activeTab)
        }).finally(() => {
          this.isLoading = false
        })
    },
    methods: {
      handlePrependEnvIconClick () {
          console.log(this.hmdFunction.environments)
          if (this.selectedEnv !== '' && typeof this.selectedEnv !== 'undefined') { this.showEnvDialog = !this.showEnvDialog } else { this.manageEnvsDialog = !this.manageEnvsDialog }
      },
      saveFunctionEnvironmentPreference () {
        FunctionService.saveFunctionEnvironmentPreference(this,this.selectedEnv,this.hmdFunction)
      },
      loadTab (activeTab) {
        var item = this.menuItems[activeTab]
        if (!item.loaded) {
          switch (item.value) {
            case 'code':
              this.loadTemplates()
              break
            case 'integration':
              FunctionService.get_api_embed(this.$route.params.identifier)
                .then(result => {
                  this.tabs = result.data
                  this.currentLanguage = this.tabs.languages[0].language
                  this.currentAction = this.tabs.languages[0].actions[0].name
                  this.codeToCopy = this.tabs.languages[0].actions[0].src
                })
              break
            case 'trigger':
              break
            case 'permission':
              break
            case 'monitoring':
              this.callMonitoring()
              break
            case 'settings':
              HmdService.label_list()
                .then(result => {
                  this.labels = result.data
                })
              break
            default:
              break
          }
        }
        this.menuItems[activeTab].loaded = true
      },
      changeCode: function (CodeContent) {
        this.code = CodeContent
      },
      changeStatus: function () {
        var func = {
          name: this.hmdFunction.name,
          enabled: this.hmdFunction.enabled,
        }
        FunctionService.update(func, this)
      },
      saveCode: function () {
        var func = {
          name: this.hmdFunction.name,
          content: this.code,
          // content: btoa(this.code)
        }
        this.saveLoadingButton = true
        FunctionService.update(func, this)
      },
      deleteTrigger (obj) {
        this.hmdFunction.triggers = this.hmdFunction.triggers.filter(function (el) {
          return (el.id !== obj.id || el.type !== obj.type)
        })
      },
      modifyPermissions (myObject) {
        if (myObject.type === 'deletePermission') {
          this.hmdFunction.permissions = this.hmdFunction.permissions.filter(function (el) {
            return el.username !== myObject.user.username
          })
        } else if (myObject.type === 'addPermission') {
          this.hmdFunction.permissions.push({
            username: myObject.user,
            perms: myObject.permission,
          })
        } else if (myObject.type === 'changePermission') {
          let currUser = this.hmdFunction.permissions.findIndex(x => x.username === myObject.user)
          this.hmdFunction.permissions[currUser].perms = myObject.permission
        }
      },
      searchHandler: function (search) {
        this.search = search
      },
      selectedItemsHandler: function (selectedItems) {
        this.selectedItems = selectedItems
      },
      updateLoadedPages (updatedComponent) {
        switch (updatedComponent) {
          case 'schema':
            this.menuItems.find(x => x.value === 'browser').loaded = false
            break
          default:
            break
        }
      },
      callMonitoring (refresh = false) {
        FunctionService.getMetrics(this, this.$route.params.name, refresh)
          .then(result => {
            this.metrics = result.data
            var count = 0
            for (var k in this.metrics) {
              this.metrics[k].options = {
                'responsive': true,
                'maintainAspectRatio': false,
                'title': {
                  'display': true,
                  'text': this.metrics[k].name,
                },
                'legend': {
                  'display': true,
                  'position': 'right',
                },
              }
              if (this.metrics.hasOwnProperty(k)) {
                for (var j in this.metrics[k].graph_data.datasets) {
                  this.metrics[k].graph_data.datasets[j].fill = false
                  this.metrics[k].graph_data.datasets[j].backgroundColor = this.$appConfig.graphColors[(count + j) % this.$appConfig.graphColors.length].backgroundColor
                  this.metrics[k].graph_data.datasets[j].borderColor = this.$appConfig.graphColors[(count + j) % this.$appConfig.graphColors.length].borderColor
                }
                ++count
              }
            }
          })
      },
      refreshMonitoringData () {
        this.callMonitoring(true)
      },
      changeAction (action) {
        this.currentAction = action
        this.changeCodeToCopy()
      },
      changeLanguage (language) {
        this.currentLanguage = language
        this.changeCodeToCopy()
      },
      changeCodeToCopy () {
        var myLanguageObject = this.tabs.languages.find(x => x.language === this.currentLanguage)
        var myActionObject = myLanguageObject.actions.find(x => x.name === this.currentAction)
        this.codeToCopy = myActionObject.src
      },
      loadTemplates () {
        var t = this
        FunctionService.getTemplates(this.hmdFunction.runtime).then(function (response) {
          t.templates = response.data.results
        }).catch(function (error) {
          // t.$store.commit('push', {msg: error.response.data, color: 'error'});
        })
      },
    },
    // If the user has the rights to modify the function and didn't saved modifications, ask him if he wants to leave and lose changes
    beforeRouteLeave (to, from, next) {
      if (this.hasWritePermissions &&
        this.lastSavedCode !== this.code &&
        !window.confirm('Do you really want to leave? You have unsaved changes!')
      ) {
        next(false)
      } else {
        next()
      }
    },
    // If the user has the rights to modify the function and didn't saved modifications, ask him if he wants to leave and lose changes
    beforeRouteUpdate (to, from, next) {
      if (this.hasWritePermissions &&
        this.lastSavedCode !== this.code &&
        !window.confirm('Do you really want to leave? You have unsaved changes!')
      ) {
        next(false)
      } else {
        next()
      }
    },
  }
</script>
<style>
</style>
